import React, { useContext, useEffect } from 'react'
import { graphql, HeadProps, PageProps, Script } from 'gatsby'
import queryString from 'query-string'

import { formatOgpUrl } from '@atoms/MicrocmsImage'
import Seo from '@src/components/Seo'
import ArticleLayout from '@src/components/ArticleLayout'
import { Article, createDescription } from '@models/Article'
import { sendPersonalizeEvent } from '@models/Personalize'
import { useTranslation, PageContext } from '@models/Translation'
import { checkNumber } from '@models/Util'
import { StoreContext } from '@models/Store'
import { addHistory } from '@models/History'
import { sendError } from '@models/Sentry'

type ArticleTemplateData = {
  article: Article
  englishArticle: Article
  allTopInfo: {
    nodes: {
      weeklyRanking: string[]
      weeklyRankingEn: string[]
      popularTags: string[]
      popularTagsEn: string[]
    }[]
  }
}

type ArticleTemplatePageContext = { isArtisan?: boolean }

const ArticleTemplate: React.FC<
  PageProps<ArticleTemplateData, ArticleTemplatePageContext>
> = ({ data, location, pageContext }) => {
  const { locale } = useTranslation()
  const { entity } = useContext(StoreContext)
  const { user } = entity
  const { blogId } = data.article
  const article = locale === 'ja' ? data.article : data.englishArticle
  const page = checkNumber(queryString.parse(location.search).page, 1)
  const { isArtisan } = pageContext

  const weeklyRanking =
    locale === 'en'
      ? data.allTopInfo.nodes[0].weeklyRankingEn
      : data.allTopInfo.nodes[0].weeklyRanking

  useEffect(() => {
    if (user === undefined) {
      return
    }

    addHistory(user.isSignIn, blogId).catch(_ =>
      sendError(Error(`履歴の保存に失敗しました: ${blogId}`)),
    )
    sendPersonalizeEvent({ itemId: blogId }, locale, user)
  }, [blogId, locale, user])

  // 英語の記事のみ存在しないのでHEADでリダイレクトする
  if (locale === 'en' && article === null) {
    return <></>
  }

  return (
    <ArticleLayout
      article={article}
      rankingArticleIds={weeklyRanking}
      page={page}
      isArtisan={isArtisan}
    />
  )
}

export const articleTemplateCommonScript: JSX.Element = (
  <>
    <Script src="https://www.instagram.com/embed.js" strategy="idle" />
    <Script src="https://platform.twitter.com/widgets.js" strategy="idle" />
  </>
)

export const Head = ({
  data,
  location,
  pageContext,
}: HeadProps<ArticleTemplateData, PageContext>) => {
  const locale = pageContext.language
  const article = locale === 'ja' ? data.article : data.englishArticle

  if (locale === 'en' && article === null) {
    return <meta httpEquiv="refresh" content={'1; url=/en'} />
  }

  return (
    <>
      <Seo
        pathName={location.pathname}
        title={article.title}
        description={createDescription(article)}
        imageUrl={formatOgpUrl(article.thumbnail.url)}
      />
      <Script
        async
        src="https://yads.c.yimg.jp/js/yads-async.js"
        strategy="idle"
      />
      {articleTemplateCommonScript}
    </>
  )
}

export default ArticleTemplate

export const query = graphql`
  query ($blogId: String) {
    article(blogId: { eq: $blogId }) {
      blogId
      path
      title
      titleWithBr
      thumbnail {
        url
        width
        height
      }
      publishDate
      parentPage {
        id
        title
        projectName
      }
      tags
      data {
        category
        headerImage {
          url
          width
          height
        }
        isSmallHeaderImage
        requester
      }
      contents {
        fieldId
        contents
        caption
        name
        color
        width
        url
        iframe
        type
        title
        image {
          url
          width
          height
        }
        list {
          id
          title
          category
          thumbnail {
            url
            width
            height
          }
          parentPage {
            id
          }
        }
      }
      totalPage
      enableVoice
      isMembersOnly
    }
    englishArticle(blogId: { eq: $blogId }) {
      blogId
      path
      title
      titleWithBr
      thumbnail {
        url
        width
        height
      }
      publishDate
      parentPage {
        id
        title
        projectName
      }
      tags
      data {
        category
        headerImage {
          url
          width
          height
        }
        isSmallHeaderImage
      }
      contents {
        fieldId
        contents
        caption
        name
        color
        width
        url
        iframe
        type
        title
        image {
          url
          width
          height
        }
        list {
          id
          title
          category
          thumbnail {
            url
            width
            height
          }
          parentPage {
            id
          }
        }
      }
      totalPage
      enableVoice
      isMembersOnly
    }
    allTopInfo {
      nodes {
        weeklyRanking
        weeklyRankingEn
      }
    }
  }
`
